import { useState } from "react";

// ─── SCANNER LOGIC ────────────────────────────────────────────────────────────
const CHECKS = [
  {
    id: "spending_limit",
    label: "No spending limit",
    severity: "critical",
    score: 25,
    keywords: ["openai", "anthropic", "api call", "llm", "gpt", "claude", "gemini", "token", "completion", "embedding"],
    antiKeywords: ["spending_limit", "max_cost", "budget", "cost_limit", "max_spend", "rate_limit"],
    description: "Your agent calls paid APIs with no cost cap. A single runaway loop can generate hundreds of API calls and thousands in charges overnight.",
    fix: "spending_limit: 100  // blocks actions over $100",
  },
  {
    id: "domain_restriction",
    label: "No domain restrictions",
    severity: "high",
    score: 20,
    keywords: ["fetch", "http", "axios", "request", "url", "endpoint", "api", "browse", "web", "search"],
    antiKeywords: ["allowed_domains", "restricted_domains", "blocked_domains", "whitelist", "allowlist"],
    description: "Your agent can make requests to any domain. No restrictions means it could exfiltrate data, call malicious endpoints, or be manipulated into accessing unauthorized services.",
    fix: "restricted_domains: ['*.unknown', '*.external']",
  },
  {
    id: "no_audit",
    label: "Zero audit trail",
    severity: "critical",
    score: 25,
    keywords: ["def ", "function ", "async ", "agent", "run", "execute", "action", "task"],
    antiKeywords: ["audit", "log", "aivil", "record", "trace", "monitor", "track"],
    description: "No record of what your agent did or decided. If it makes a mistake, leaks data, or acts outside its mandate — you have zero evidence of what happened. This is a legal liability.",
    fix: "await aivil.audit(agent.id, action)  // logs every decision",
  },
  {
    id: "human_escalation",
    label: "No human escalation path",
    severity: "high",
    score: 15,
    keywords: ["purchase", "buy", "payment", "send", "delete", "remove", "deploy", "transfer", "write", "post", "email"],
    antiKeywords: ["human", "approve", "escalate", "signoff", "review", "confirm", "notify", "alert"],
    description: "Your agent takes consequential actions (purchases, sends, deletes) with no human checkpoint. One hallucination and it acts unilaterally on bad information.",
    fix: "requires_human_signoff_over: 50  // escalates above $50",
  },
  {
    id: "infinite_loop",
    label: "Infinite loop risk",
    severity: "high",
    score: 15,
    keywords: ["while", "loop", "retry", "recursive", "recursion", "repeat", "cycle"],
    antiKeywords: ["max_retries", "max_iterations", "break", "limit", "count", "timeout", "max_requests"],
    description: "Your code contains loop or retry patterns with no visible iteration limit. A failed condition can cause infinite execution, burning API credits and compute until manually stopped.",
    fix: "max_requests_per_hour: 200  // rate limits the agent",
  },
  {
    id: "data_access",
    label: "Unguarded data access",
    severity: "critical",
    score: 20,
    keywords: ["database", "db.", "sql", "query", "select", "supabase", "mongo", "postgres", "mysql", "redis", "read", "write"],
    antiKeywords: ["readonly", "read_only", "allowed_topics", "blocked_topics", "permission", "role"],
    description: "Your agent has database or storage access with no policy controls. A hallucination or prompt injection could result in unauthorized data reads or writes.",
    fix: "allowed_topics: ['read_only']  // restricts what agent can do",
  },
  {
    id: "no_identity",
    label: "No verified identity",
    severity: "medium",
    score: 10,
    keywords: ["agent", "bot", "assistant", "system_prompt", "role:", "persona"],
    antiKeywords: ["did:", "aivil", "agent_id", "registry", "birth_certificate", "verified"],
    description: "Your agent has no cryptographic identity. If it contacts external services or users, there is no way to verify it is authorized. Third parties cannot trust it.",
    fix: "const { agent } = await aivil.createAgent({...})",
  },
];

const SEVERITIES = {
  critical: { color: "#ff4d6a", bg: "rgba(255,77,106,0.08)", border: "rgba(255,77,106,0.3)", label: "CRITICAL" },
  high:     { color: "#ff8c00", bg: "rgba(255,140,0,0.08)",  border: "rgba(255,140,0,0.3)",  label: "HIGH" },
  medium:   { color: "#c9a84c", bg: "rgba(201,168,76,0.08)", border: "rgba(201,168,76,0.3)", label: "MEDIUM" },
};

const C = {
  bg: "#04060c", surface: "#080c14", card: "#0d1220",
  border: "#151f30", gold: "#c9a84c", goldDim: "rgba(201,168,76,0.08)",
  text: "#dde4f0", textDim: "#3d5070", textMid: "#7a90b8",
  green: "#00d68f", red: "#ff4d6a",
};

const PLACEHOLDER = `# Paste your agent code or system prompt here
# Example:

async def research_agent(topic):
    while True:
        result = await openai.chat.completions.create(
            model="gpt-4",
            messages=[{"role": "user", "content": f"Search for {topic}"}]
        )
        response = await fetch(f"https://api.external.com/data")
        await database.query(f"INSERT INTO results VALUES ({result})")
        if "done" in result.choices[0].message.content:
            break`;

function analyzeCode(code) {
  const lower = code.toLowerCase();
  const found = [];
  let totalScore = 0;

  for (const check of CHECKS) {
    const hasRisk    = check.keywords.some(k => lower.includes(k));
    const hasFix     = check.antiKeywords.some(k => lower.includes(k));
    if (hasRisk && !hasFix) {
      found.push(check);
      totalScore += check.score;
    }
  }

  // Normalize to 0-10
  const maxPossible = CHECKS.reduce((s, c) => s + c.score, 0);
  const normalized  = Math.min(10, (totalScore / maxPossible) * 10).toFixed(1);

  return { issues: found, score: parseFloat(normalized), rawScore: totalScore };
}

function getRiskLabel(score) {
  if (score >= 7)  return { label: "CRITICAL RISK",  color: "#ff4d6a" };
  if (score >= 5)  return { label: "HIGH RISK",       color: "#ff8c00" };
  if (score >= 3)  return { label: "MEDIUM RISK",     color: "#c9a84c" };
  return              { label: "LOW RISK",         color: "#00d68f" };
}

// ─── SCANNER COMPONENT ────────────────────────────────────────────────────────
export default function VulnerabilityScanner() {
  const [code,     setCode]     = useState("");
  const [result,   setResult]   = useState(null);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const scan = () => {
    if (!code.trim()) return;
    setScanning(true);
    setResult(null);
    // Simulate scanning delay for effect
    setTimeout(() => {
      setResult(analyzeCode(code));
      setScanning(false);
    }, 1200);
  };

  const risk = result ? getRiskLabel(result.score) : null;

  return (
    <section style={{ padding: "120px 48px", maxWidth: 900, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div style={{ fontSize: 10, color: C.gold, letterSpacing: 4, fontFamily: "'JetBrains Mono',monospace", marginBottom: 16 }}>
          FREE TOOL
        </div>
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(32px,4vw,56px)", fontWeight: 300, lineHeight: 1.2, marginBottom: 16 }}>
          AI Agent Security Audit
        </h2>
        <p style={{ fontSize: 15, color: C.textMid, maxWidth: 560, margin: "0 auto", lineHeight: 1.8 }}>
          Paste your agent code or system prompt. We'll scan for vulnerabilities that could cause runaway costs, data leaks, or uncontrolled actions.
        </p>
      </div>

      {/* Input area */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 10, color: C.textDim, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2 }}>AGENT CODE / SYSTEM PROMPT</span>
          <span style={{ fontSize: 10, color: C.textDim, fontFamily: "'JetBrains Mono',monospace" }}>{code.length} chars</span>
        </div>
        <textarea
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder={PLACEHOLDER}
          style={{
            width: "100%", minHeight: 220, background: "transparent", border: "none",
            outline: "none", color: C.text, fontFamily: "'JetBrains Mono',monospace",
            fontSize: 12, lineHeight: 1.8, padding: "16px", resize: "vertical",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Scan button */}
      <div style={{ display: "flex", gap: 12, marginBottom: 40 }}>
        <button
          onClick={scan}
          disabled={!code.trim() || scanning}
          style={{
            flex: 1, background: code.trim() && !scanning ? C.gold : "#1a2540",
            border: "none", borderRadius: 4, color: code.trim() && !scanning ? C.bg : C.textDim,
            padding: "14px", cursor: code.trim() && !scanning ? "pointer" : "not-allowed",
            fontSize: 11, letterSpacing: 2, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700,
            transition: "all 0.2s",
          }}
        >
          {scanning ? "SCANNING..." : "SCAN FOR VULNERABILITIES →"}
        </button>
        {code && (
          <button
            onClick={() => { setCode(""); setResult(null); }}
            style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textDim, padding: "14px 20px", cursor: "pointer", fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}
          >
            CLEAR
          </button>
        )}
      </div>

      {/* Scanning animation */}
      {scanning && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ fontSize: 11, color: C.gold, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 16 }}>
            ANALYZING AGENT ARCHITECTURE...
          </div>
          {["Checking spending controls...", "Scanning domain restrictions...", "Detecting audit gaps...", "Analyzing loop patterns..."].map((msg, i) => (
            <div key={i} style={{ fontSize: 11, color: C.textDim, fontFamily: "'JetBrains Mono',monospace", marginBottom: 6, opacity: 0.7 }}>
              {msg}
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {result && !scanning && (
        <div style={{ animation: "fadeIn 0.4s ease" }}>

          {/* Score */}
          <div style={{
            background: C.card, border: `1px solid ${risk.color}44`,
            borderRadius: 8, padding: 28, marginBottom: 24, textAlign: "center"
          }}>
            <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>
              VULNERABILITY SCORE
            </div>
            <div style={{ fontSize: 72, fontFamily: "'Cormorant Garamond',serif", fontWeight: 300, color: risk.color, lineHeight: 1, marginBottom: 8 }}>
              {result.score.toFixed(1)}
              <span style={{ fontSize: 24, color: C.textDim }}>/10</span>
            </div>
            <div style={{ fontSize: 13, color: risk.color, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 3, marginBottom: 16 }}>
              {risk.label}
            </div>
            <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.7 }}>
              {result.issues.length === 0
                ? "✓ No major vulnerabilities detected. Your agent looks well-protected."
                : `Found ${result.issues.length} vulnerability${result.issues.length > 1 ? "s" : ""} that could cause ${
                    result.issues.some(i => i.id === "spending_limit") ? "runaway costs, " : ""
                  }${result.issues.some(i => i.id === "data_access") ? "data leaks, " : ""}uncontrolled actions.`
              }
            </div>
          </div>

          {/* Issues list */}
          {result.issues.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 3, fontFamily: "'JetBrains Mono',monospace", marginBottom: 16 }}>
                VULNERABILITIES FOUND
              </div>
              {result.issues.map((issue, i) => {
                const sev = SEVERITIES[issue.severity];
                const open = expanded === issue.id;
                return (
                  <div
                    key={issue.id}
                    style={{ background: sev.bg, border: `1px solid ${sev.border}`, borderRadius: 6, marginBottom: 8, overflow: "hidden", cursor: "pointer" }}
                    onClick={() => setExpanded(open ? null : issue.id)}
                  >
                    <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: 9, color: sev.color, background: `${sev.color}22`, border: `1px solid ${sev.color}44`, padding: "2px 8px", borderRadius: 3, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 1 }}>
                          {sev.label}
                        </span>
                        <span style={{ fontSize: 13, color: C.text, fontFamily: "'JetBrains Mono',monospace" }}>
                          {issue.label}
                        </span>
                      </div>
                      <span style={{ fontSize: 14, color: C.textDim }}>
                        {open ? "▲" : "▼"}
                      </span>
                    </div>
                    {open && (
                      <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${sev.border}` }}>
                        <p style={{ fontSize: 12, color: C.textMid, lineHeight: 1.8, marginTop: 12, marginBottom: 12 }}>
                          {issue.description}
                        </p>
                        <div style={{ background: "#030508", borderRadius: 4, padding: "10px 14px" }}>
                          <div style={{ fontSize: 9, color: C.textDim, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 6 }}>
                            FIX WITH AIVIL:
                          </div>
                          <code style={{ fontSize: 11, color: C.green, fontFamily: "'JetBrains Mono',monospace" }}>
                            {issue.fix}
                          </code>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA */}
          {result.issues.length > 0 && (
            <div style={{ background: C.goldDim, border: `1px solid ${C.gold}33`, borderRadius: 8, padding: 28, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: C.gold, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 8 }}>
                FIX ALL {result.issues.length} VULNERABILITIES IN 3 LINES
              </div>
              <div style={{ fontSize: 13, color: C.textMid, marginBottom: 20, lineHeight: 1.8 }}>
                AIVIL adds spending limits, domain controls, audit trails, and human escalation to any AI agent.
              </div>
              <div style={{ background: "#030508", border: `1px solid ${C.border}`, borderRadius: 6, padding: "12px 20px", display: "inline-block", marginBottom: 20, fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: C.text }}>
                npm install aivil
              </div>
              <br />
              <button
                onClick={() => window.goToSignup && window.goToSignup()}
                style={{ background: C.gold, border: "none", borderRadius: 4, color: C.bg, padding: "12px 32px", fontSize: 11, letterSpacing: 2, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, cursor: "pointer" }}
              >
                GET FREE API KEY →
              </button>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: 12, fontFamily: "'JetBrains Mono',monospace" }}>
                Free forever · No credit card · 2 min setup
              </div>
            </div>
          )}

          {result.issues.length === 0 && (
            <div style={{ background: "rgba(0,214,143,0.06)", border: `1px solid ${C.green}33`, borderRadius: 8, padding: 28, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: C.green, fontFamily: "'JetBrains Mono',monospace", letterSpacing: 2, marginBottom: 8 }}>
                ✓ YOUR AGENT LOOKS PROTECTED
              </div>
              <div style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>
                Add AIVIL to get a cryptographic audit trail and live dashboard for every decision.
              </div>
              <button
                onClick={() => window.goToSignup && window.goToSignup()}
                style={{ background: C.gold, border: "none", borderRadius: 4, color: C.bg, padding: "12px 32px", fontSize: 11, letterSpacing: 2, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, cursor: "pointer" }}
              >
                GET FREE API KEY →
              </button>
            </div>
          )}

          <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:none } }`}</style>
        </div>
      )}
    </section>
  );
}
